import { useWizard } from 'react-use-wizard';
import { motion } from 'framer-motion';
import { AnimatedStep } from '../AnimatedStep';
import type { SetupData } from '../SetupWizard';
import type { DayOfWeek } from 'shared';

interface Props {
  data: SetupData;
  updateData: (updates: Partial<SetupData>) => void;
}

const DAYS: { value: DayOfWeek; label: string }[] = [
  { value: 'MON', label: 'Mon' },
  { value: 'TUE', label: 'Tue' },
  { value: 'WED', label: 'Wed' },
  { value: 'THU', label: 'Thu' },
  { value: 'FRI', label: 'Fri' },
  { value: 'SAT', label: 'Sat' },
  { value: 'SUN', label: 'Sun' },
];

export function WorkDaysStep({ data, updateData }: Props) {
  const { nextStep, previousStep } = useWizard();

  const toggleDay = (day: DayOfWeek) => {
    const current = data.work_days;
    const updated = current.includes(day)
      ? current.filter((d) => d !== day)
      : [...current, day];
    updateData({ work_days: updated });
  };

  const canContinue = data.work_days.length > 0;

  return (
    <AnimatedStep>
      <div className="space-y-6">
        <div>
          <h2 className="text-2xl font-bold text-gray-900 mb-2">
            Which days do you work?
          </h2>
          <p className="text-gray-600">
            Select the days you're available for store visits.
          </p>
        </div>

        <div className="grid grid-cols-7 gap-2">
          {DAYS.map((day, index) => {
            const isSelected = data.work_days.includes(day.value);
            return (
              <motion.button
                key={day.value}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.05 }}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={() => toggleDay(day.value)}
                className={`
                  py-4 rounded-xl font-semibold text-sm transition-all
                  ${isSelected
                    ? 'bg-blue-600 text-white shadow-md'
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                  }
                `}
              >
                {day.label}
              </motion.button>
            );
          })}
        </div>

        <p className="text-sm text-gray-500 text-center">
          {data.work_days.length} day{data.work_days.length !== 1 ? 's' : ''} selected
        </p>

        <div className="flex gap-3">
          <button
            onClick={() => previousStep()}
            className="px-6 py-3 text-gray-600 font-medium hover:text-gray-900 transition-colors"
          >
            Back
          </button>
          <button
            onClick={() => nextStep()}
            disabled={!canContinue}
            className="flex-1 px-6 py-3 bg-blue-600 text-white font-semibold rounded-xl hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            Continue
          </button>
        </div>
      </div>
    </AnimatedStep>
  );
}
